import React, { useMemo, useState, useRef, useEffect } from "react";
import styles from "./CategoryManagement.module.css";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiService } from "../../services/api";
import { Category, Tab } from "../../types";
import { useNavigate } from "react-router-dom";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

interface FormState {
  id?: string;
  nameRu: string;
  nameEn?: string;
  descriptionRu?: string;
  descriptionEn?: string;
  tabId?: number;
}

export default function CategoryManagement() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const modalContentRef = useRef<HTMLDivElement>(null);

  const { data: categories, isLoading } = useQuery({
    queryKey: ["categories"],
    queryFn: () => apiService.getCategories(),
  });

  const { data: tabs } = useQuery({
    queryKey: ["tabs"],
    queryFn: () => apiService.getTabs(),
  });

  const [filterName, setFilterName] = useState<string>("");
  const [filterTabId, setFilterTabId] = useState<string>("");

  const filteredCategories = useMemo(() => {
    if (!categories) return [];
    return categories
      .filter((c) => {
        const matchesName = c.nameRu
          .toLowerCase()
          .includes(filterName.toLowerCase().trim());
        const matchesTab =
          filterTabId === "" || String(c.tabId) === filterTabId;
        return matchesName && matchesTab;
      })
      .sort((a, b) => {
        const orderA = (a as any).sortOrder ?? 0;
        const orderB = (b as any).sortOrder ?? 0;
        if (orderA !== orderB) {
          return orderA - orderB;
        }
        return parseInt(a.id) - parseInt(b.id);
      });
  }, [categories, filterName, filterTabId]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const updateOrderMutation = useMutation({
    mutationFn: async (categories: { id: string; sortOrder: number }[]) => {
      return apiService.updateCategoriesOrder(categories);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["categories"] });
    },
  });

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = filteredCategories.findIndex((c) => c.id === active.id);
      const newIndex = filteredCategories.findIndex((c) => c.id === over.id);

      const newCategories = arrayMove(filteredCategories, oldIndex, newIndex);
      const categoriesWithOrder = newCategories.map((category, index) => ({
        id: category.id,
        sortOrder: index + 1,
      }));

      updateOrderMutation.mutate(categoriesWithOrder);
    }
  };

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formState, setFormState] = useState<FormState>({
    nameRu: "",
  });
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<string>("");
  const [formError, setFormError] = useState<string>("");

  const resetForm = () => {
    setFormState({ nameRu: "" });
    setFormError("");
  };

  const openCreate = () => {
    resetForm();
    setError("");
    setFormError("");
    createMutation.reset();
    updateMutation.reset();
    setIsFormOpen(true);
  };

  const openEdit = (category: Category) => {
    setFormState({
      id: category.id,
      nameRu: category.nameRu || "",
      nameEn: category.nameEn || "",
      descriptionRu: category.descriptionRu || "",
      descriptionEn: category.descriptionEn || "",
      tabId: category.tabId,
    });
    setError("");
    setFormError("");
    createMutation.reset();
    updateMutation.reset();
    setIsFormOpen(true);
  };

  const closeForm = () => {
    setIsFormOpen(false);
    resetForm();
  };

  const createMutation = useMutation({
    mutationFn: async (payload: FormState) => {
      // Map to backend expected structure
      const body: any = {
        name: { ru: payload.nameRu, en: payload.nameEn || undefined },
        description:
          payload.descriptionRu || payload.descriptionEn
            ? {
                ru: payload.descriptionRu || "",
                en: payload.descriptionEn || undefined,
              }
            : undefined,
        tabId: payload.tabId,
      };
      return apiService.createCategory(body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      closeForm();
    },
    onError: (error: any) => {
      const errorMessage =
        error.response?.data?.message || "Ошибка при создании категории";
      setFormError(errorMessage);
      setTimeout(() => setFormError(""), 5000);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (payload: FormState) => {
      if (!payload.id) throw new Error("Missing id");
      const body: any = {
        name: { ru: payload.nameRu, en: payload.nameEn || undefined },
        description:
          payload.descriptionRu || payload.descriptionEn
            ? {
                ru: payload.descriptionRu || "",
                en: payload.descriptionEn || undefined,
              }
            : undefined,
        tabId: payload.tabId,
      };
      return apiService.updateCategory(payload.id, body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      closeForm();
    },
    onError: (error: any) => {
      const errorMessage =
        error.response?.data?.message || "Ошибка при обновлении категории";
      setFormError(errorMessage);
      setTimeout(() => setFormError(""), 5000);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => apiService.deleteCategory(id),
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      queryClient.invalidateQueries({ queryKey: ["shops"] });
      setError("");
      const message = data?.message || "Категория успешно удалена";
      setSuccess(message);
      setTimeout(() => setSuccess(""), 5000);
    },
    onError: (error: any) => {
      const errorMessage =
        error.response?.data?.message || "Ошибка при удалении категории";
      setError(errorMessage);
      setSuccess("");
      setTimeout(() => setError(""), 5000);
    },
  });

  const tabsOptions = useMemo(() => tabs || [], [tabs]);
  const getTabNameRu = (id?: number) => {
    if (!id) return "—";
    const found = tabsOptions.find((t: Tab) => String(t.id) === String(id));
    return found ? found.name.ru : String(id);
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const validationMessages: string[] = [];

    // Локальная проверка обязательных полей, как в модалке "Добавить объект"
    if (!formState.nameRu || !formState.nameRu.trim()) {
      validationMessages.push(
        "• Название (RU): обязательно для заполнения и не может быть пустым"
      );
    }

    if (!formState.tabId) {
      validationMessages.push("• Вкладка: обязательно для заполнения");
    }

    if (validationMessages.length > 0) {
      setFormError(`Ошибки валидации:\n${validationMessages.join("\n")}`);
      return;
    }

    // Проверка уникальности комбинации название-вкладка
    if (formState.nameRu && formState.tabId && categories) {
      const isDuplicate = categories.some((c: Category) => {
        const sameName =
          c.nameRu?.toLowerCase().trim() ===
          formState.nameRu.toLowerCase().trim();
        const sameTab = c.tabId === formState.tabId;
        const isDifferentCategory = formState.id ? c.id !== formState.id : true;
        return sameName && sameTab && isDifferentCategory;
      });

      if (isDuplicate) {
        setFormError("Данное название уже существует, для текущей вкладки");
        setTimeout(() => setFormError(""), 5000);
        return;
      }
    }

    setFormError("");
    if (formState.id) {
      updateMutation.mutate(formState);
    } else {
      createMutation.mutate(formState);
    }
  };

  const handleDelete = (category: Category) => {
    if (
      window.confirm(
        "Удаление Категории приведет к удалению связанных Объектов. Удалить Категорию? "
      )
    ) {
      deleteMutation.mutate(category.id);
    }
  };

  useEffect(() => {
    if (isFormOpen && modalContentRef.current) {
      modalContentRef.current.scrollTop = 0;
    }
  }, [isFormOpen]);

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div className={styles.logo}>Управление категориями</div>
        <button
          className={styles.backButton}
          onClick={() => navigate("/admin")}
        >
          Назад
        </button>
      </header>

      <main className={styles.mainContent}>
        <h1 className={styles.pageTitle}>Категории</h1>

        <div className={styles.controlsRow}>
          <button className={styles.addButton} onClick={openCreate}>
            + Добавить категорию
          </button>
          <div className={styles.filtersContainer}>
            <div className={styles.filterContainer}>
              <input
                type="text"
                className={styles.filterInput}
                placeholder="Поиск по названию категории..."
                value={filterName}
                onChange={(e) => setFilterName(e.target.value)}
              />
            </div>
            <div className={styles.filterContainer}>
              <select
                className={styles.filterSelect}
                value={filterTabId}
                onChange={(e) => setFilterTabId(e.target.value)}
              >
                <option value="">Все вкладки</option>
                {tabsOptions
                  .filter((t: Tab) => t.isEditable === true)
                  .map((t: Tab) => (
                    <option key={t.id} value={t.id}>
                      {t.name.ru}
                    </option>
                  ))}
              </select>
            </div>
          </div>
        </div>

        {error && <div className={styles.errorMessage}>{error}</div>}
        {success && <div className={styles.successMessage}>{success}</div>}

        {isLoading ? (
          <div className={styles.loadingSpinner}>Загрузка...</div>
        ) : filteredCategories && filteredCategories.length > 0 ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <div className={styles.table}>
              <div className={styles.tableHeader}>
                <div className={styles.tableHeaderCell}></div>
                <div className={styles.tableHeaderCell}>Название (RU)</div>
                <div className={styles.tableHeaderCell}>Название (EN)</div>
                <div className={styles.tableHeaderCell}>Вкладка</div>
                <div className={styles.tableHeaderCell}>Действия</div>
              </div>
              <SortableContext
                items={filteredCategories.map((c) => c.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className={styles.tableBody}>
                  {filteredCategories.map((c) => (
                    <SortableCategoryItem
                      key={c.id}
                      category={c}
                      onEdit={() => openEdit(c)}
                      onDelete={() => handleDelete(c)}
                      getTabNameRu={getTabNameRu}
                    />
                  ))}
                </div>
              </SortableContext>
            </div>
          </DndContext>
        ) : (
          <div className={styles.emptyState}>Категории отсутствуют</div>
        )}

        <div
          className={`${styles.modal} ${isFormOpen ? styles.modalOpen : ""}`}
        >
          <div
            ref={modalContentRef}
            className={styles.modalContent}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className={styles.modalTitle}>
              {formState.id ? "Редактировать категорию" : "Создать категорию"}
            </h2>
            {formError && (
              <div
                className={styles.errorMessage}
                style={{ whiteSpace: "pre-line" }}
              >
                {formError}
              </div>
            )}
            <form onSubmit={onSubmit}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Название (RU)*</label>
                <input
                  className={styles.input}
                  value={formState.nameRu}
                  onChange={(e) =>
                    setFormState((s) => ({ ...s, nameRu: e.target.value }))
                  }
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Название (EN)</label>
                <input
                  className={styles.input}
                  value={formState.nameEn || ""}
                  onChange={(e) =>
                    setFormState((s) => ({ ...s, nameEn: e.target.value }))
                  }
                />
              </div>
              {/* <div className={styles.formGroup}>
                <label className={styles.label}>Описание (RU)</label>
                <textarea
                  className={styles.textArea}
                  value={formState.descriptionRu || ""}
                  onChange={(e) =>
                    setFormState((s) => ({
                      ...s,
                      descriptionRu: e.target.value,
                    }))
                  }
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Описание (EN)</label>
                <textarea
                  className={styles.textArea}
                  value={formState.descriptionEn || ""}
                  onChange={(e) =>
                    setFormState((s) => ({
                      ...s,
                      descriptionEn: e.target.value,
                    }))
                  }
                />
              </div> */}
              <div className={styles.formGroup}>
                <label className={styles.label}>Вкладка*</label>
                <select
                  className={styles.select}
                  value={formState.tabId ?? ""}
                  onChange={(e) =>
                    setFormState((s) => ({
                      ...s,
                      tabId: e.target.value
                        ? parseInt(e.target.value, 10)
                        : undefined,
                    }))
                  }
                >
                  <option value="">—</option>
                  {tabsOptions
                    .filter((t: Tab) => t.isEditable === true)
                    .map((t: Tab) => (
                      <option key={t.id} value={t.id}>
                        {t.name.ru}
                      </option>
                    ))}
                </select>
              </div>
              <div className={styles.modalActions}>
                <button
                  type="button"
                  className={`${styles.actionButton} ${styles.actionWarning}`}
                  onClick={closeForm}
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  className={styles.actionButton}
                  disabled={
                    createMutation.isPending || updateMutation.isPending
                  }
                >
                  {formState.id ? "Сохранить" : "Создать"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
}

interface SortableCategoryItemProps {
  category: Category;
  onEdit: () => void;
  onDelete: () => void;
  getTabNameRu: (id?: number) => string;
}

function SortableCategoryItem({
  category,
  onEdit,
  onDelete,
  getTabNameRu,
}: SortableCategoryItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: category.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`${styles.tableRow} ${isDragging ? styles.dragging : ""}`}
    >
      <div className={styles.dragHandle} {...attributes} {...listeners}>
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="7" cy="5" r="1.5" fill="currentColor" />
          <circle cx="13" cy="5" r="1.5" fill="currentColor" />
          <circle cx="7" cy="10" r="1.5" fill="currentColor" />
          <circle cx="13" cy="10" r="1.5" fill="currentColor" />
          <circle cx="7" cy="15" r="1.5" fill="currentColor" />
          <circle cx="13" cy="15" r="1.5" fill="currentColor" />
        </svg>
      </div>
      <div className={styles.tableCell}>{category.nameRu}</div>
      <div className={styles.tableCell}>{category.nameEn || "—"}</div>
      <div className={styles.tableCell}>{getTabNameRu(category.tabId)}</div>
      <div className={`${styles.tableCell} ${styles.actionCell}`}>
        <button className={styles.actionButton} onClick={onEdit}>
          Редактировать
        </button>
        <button
          className={`${styles.actionButton} ${styles.actionDanger}`}
          onClick={onDelete}
        >
          Удалить
        </button>
      </div>
    </div>
  );
}
